import React, { useCallback, useContext, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import CopyButtonCDS from '@sharedCR/coreDesignSystem/Buttons/CopyButtonCDS';
import { snapshotPaneStyles } from 'app/shared/modules/EntityAuditHistory/EntityAuditHistory.styles';
import { prettyPrintSnapshot } from 'app/shared/modules/EntityAuditHistory/EntityAuditHistory.utils';
import useCopyWithNotice from 'app/shared/modules/EntityAuditHistory/hooks/useCopyWithNotice';
import PropTypes from 'prop-types';

import {
  COPY_RAW_JSON_LABEL,
  RAW_JSON_COPIED_MESSAGE,
  RAW_JSON_LOAD_FAILED_MESSAGE,
  RAW_JSON_LOADING_LABEL,
  TEMPLATE_SIDE_LABEL,
  TemplateRequestStatus,
} from '../../FormChangeHistory.constants';
import { FormTemplateRequestsContext } from '../../FormChangeHistory.context';
import { FormChangeHistoryTestIds } from '../../FormChangeHistory.testIds';
import useTemplateRequest from '../../hooks/useTemplateRequest';
import TemplateRequestState from '../TemplateRequestState';

import { paneHeaderStyles, rawJsonBlockStyles } from './RawTemplateJsonPane.styles';

/** One side's Prism document, pretty-printed read-only, with a copy action. */
const RawTemplateJsonPane = ({ auditId, side }) => {
  const copyWithNotice = useCopyWithNotice();
  const requests = useContext(FormTemplateRequestsContext);

  const loadJson = useCallback(() => requests.getJson(auditId, side), [requests, auditId, side]);

  const { status, data, retry } = useTemplateRequest(loadJson);

  const json = useMemo(
    () => (status === TemplateRequestStatus.LOADED ? prettyPrintSnapshot(data) : ''),
    [status, data],
  );

  const onCopy = useCallback(
    () => copyWithNotice(json, RAW_JSON_COPIED_MESSAGE),
    [copyWithNotice, json],
  );

  return (
    <Box
      data-side={side}
      data-testid={FormChangeHistoryTestIds.RAW_JSON_PANE}
      sx={snapshotPaneStyles}
    >
      <Box sx={paneHeaderStyles}>
        <Typography color="text.secondary" variant="medium12">
          {TEMPLATE_SIDE_LABEL[side]}
        </Typography>
        {status === TemplateRequestStatus.LOADED && (
          <Box data-testid={FormChangeHistoryTestIds.RAW_JSON_COPY}>
            <CopyButtonCDS label={COPY_RAW_JSON_LABEL[side]} onClick={onCopy} />
          </Box>
        )}
      </Box>

      {status === TemplateRequestStatus.LOADED ? (
        <Typography component="pre" sx={rawJsonBlockStyles} variant="regular12">
          {json}
        </Typography>
      ) : (
        <TemplateRequestState
          failedMessage={RAW_JSON_LOAD_FAILED_MESSAGE}
          loadingLabel={RAW_JSON_LOADING_LABEL}
          status={status}
          onRetry={retry}
        />
      )}
    </Box>
  );
};

RawTemplateJsonPane.propTypes = {
  auditId: PropTypes.string.isRequired,
  side: PropTypes.string.isRequired,
};

export default RawTemplateJsonPane;
